import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Button } from './ui/button';
import { blogPosts } from './BlogSection';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { useEffect } from 'react';

export const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center bg-[#F8FAFC]">
          <div className="text-center">
            <h1 className="font-oswald text-4xl font-bold text-[#385723] mb-4">
              Artículo no encontrado
            </h1>
            <p className="font-manrope text-slate-600 mb-8">
              El artículo que buscas no existe o ha sido eliminado.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-[#385723] text-white hover:bg-[#2a421b]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al inicio
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const otherPosts = blogPosts.filter((p) => p.id !== post.id).slice(0, 2);

  const shareUrl = window.location.href;
  const shareTitle = post.title;

  const handleShare = (platform) => {
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      
      {/* Hero Image */}
      <div className="relative h-[50vh] md:h-[60vh] mt-20">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
          data-testid="blog-post-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        {/* Category Badge */}
        <div className="absolute top-8 left-8">
          <span className="bg-[#92D050] text-[#385723] font-manrope text-sm font-bold uppercase tracking-wider px-4 py-2">
            {post.category}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10">
        <article className="bg-white shadow-xl p-8 md:p-12">
          {/* Back Button */}
          <Link
            to="/#blog"
            data-testid="back-to-blog"
            className="inline-flex items-center text-[#385723] font-manrope font-semibold text-sm hover:text-[#92D050] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Blog
          </Link>

          {/* Meta */}
          <div className="flex items-center gap-6 mb-6 text-slate-500">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span className="font-manrope text-sm">{post.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span className="font-manrope text-sm">{post.readTime} de lectura</span>
            </div>
          </div>

          {/* Title */}
          <h1
            data-testid="blog-post-title"
            className="font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-[#385723] uppercase tracking-tight leading-tight mb-8"
          >
            {post.title}
          </h1>

          {/* Gold Line Divider */}
          <div className="gold-line mb-8" />

          {/* Content */}
          <div
            data-testid="blog-post-content"
            className="font-manrope text-slate-700 text-lg leading-relaxed space-y-6"
          >
            {post.content.split('\n\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          {/* Share Section */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-[#385723]" />
                <span className="font-manrope font-semibold text-[#385723]">
                  Compartir este artículo:
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleShare('facebook')}
                  data-testid="share-facebook"
                  className="w-10 h-10 bg-[#385723] text-white flex items-center justify-center hover:bg-[#92D050] hover:text-[#385723] transition-all duration-300"
                >
                  <Facebook className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleShare('twitter')}
                  data-testid="share-twitter"
                  className="w-10 h-10 bg-[#385723] text-white flex items-center justify-center hover:bg-[#92D050] hover:text-[#385723] transition-all duration-300"
                >
                  <Twitter className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleShare('linkedin')}
                  data-testid="share-linkedin"
                  className="w-10 h-10 bg-[#385723] text-white flex items-center justify-center hover:bg-[#92D050] hover:text-[#385723] transition-all duration-300"
                >
                  <Linkedin className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </article>

        {/* Related Posts */}
        {otherPosts.length > 0 && (
          <div className="mt-16 mb-20">
            <h2 className="font-oswald text-2xl font-bold text-[#385723] uppercase tracking-tight mb-8">
              Otros <span className="text-[#92D050]">Artículos</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {otherPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  data-testid={`related-post-${relatedPost.id}`}
                  className="blog-card bg-white group block shadow-md hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-[#92D050] text-[#385723] font-manrope text-xs font-bold uppercase tracking-wider px-3 py-1">
                        {relatedPost.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-oswald text-lg font-bold text-[#385723] uppercase tracking-tight group-hover:text-[#92D050] transition-colors duration-300 line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="font-manrope text-slate-600 text-sm mt-2 line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BlogPost;
